import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tagNameField", "preview", "previewContent", "submitBtn", "errors"]
  static values = { changeUrl: String }

  tagNameSelected(event) {
    const { id, name, address, city, state, zipcode } = event.detail

    this.tagNameFieldTarget.value = id

    const addressParts = [address, city, state, zipcode].filter(Boolean).join(", ")
    this.previewContentTarget.innerHTML = ""

    const nameEl = document.createElement("p")
    nameEl.className = "font-medium text-text-heading"
    nameEl.textContent = name || ""
    this.previewContentTarget.appendChild(nameEl)

    if (addressParts) {
      const addrEl = document.createElement("p")
      addrEl.className = "text-text-muted mt-0.5"
      addrEl.textContent = addressParts
      this.previewContentTarget.appendChild(addrEl)
    }

    this.previewTarget.classList.remove("hidden")
    this.submitBtnTarget.disabled = false
    this._hideErrors()
  }

  propertyCreated(event) {
    if (!event.detail) return
    // Only act if this modal is actually open
    if (this.element.classList.contains("hidden")) return

    const tagName = event.detail
    this.tagNameFieldTarget.value = tagName.id
    this._doSave()
  }

  async save() {
    await this._doSave()
  }

  async _doSave() {
    const tagNameId = this.tagNameFieldTarget.value
    if (!tagNameId) {
      this._showErrors(["Please select a property"])
      return
    }

    this._hideErrors()
    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.textContent = "Saving..."

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(this.changeUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({ tag_name_id: tagNameId })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Update the Location card
        const card = document.getElementById("location-card-body")
        if (card) {
          this._updateField(card, "property", data.property)
          this._updateField(card, "address", data.full_address)
        }

        // Close the edit-location modal and update its fields for next open
        const editModal = document.querySelector("[data-controller*='edit-location']")
        if (editModal) {
          editModal.classList.add("hidden")
          const ctrl = this.application.getControllerForElementAndIdentifier(editModal, "edit-location")
          if (ctrl) {
            ctrl.updateFields({
              code: data.code,
              name: data.name,
              address: data.address,
              city: data.city,
              state: data.state,
              zipcode: data.zipcode
            })
          }
        }

        if (data.project_name) this._updateProjectName(data.project_name)

        this._closeAndReset()
        this._showFlash("Property changed successfully.")
      } else {
        this._showErrors(data.errors || ["Failed to change property"])
        this.submitBtnTarget.disabled = false
        this.submitBtnTarget.textContent = "Assign Property"
      }
    } catch {
      this._showErrors(["A network error occurred. Please try again."])
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.textContent = "Assign Property"
    }
  }

  _updateField(container, fieldName, value) {
    const el = container.querySelector(`[data-field="${fieldName}"]`)
    if (!el) return
    const label = el.querySelector("span")
    if (label) {
      el.textContent = ""
      el.appendChild(label)
      el.append(` ${value || "—"}`)
    } else {
      el.textContent = value || "—"
    }
  }

  _updateProjectName(name) {
    const header = document.querySelector("[data-project-name]")
    if (header) header.textContent = name

    const breadcrumb = document.querySelector("[data-breadcrumb-project-name]")
    if (breadcrumb) breadcrumb.textContent = name.length > 40 ? name.substring(0, 37) + "..." : name

    document.title = name
  }

  _closeAndReset() {
    this.element.classList.add("hidden")
    document.body.style.overflow = ""

    // Reset the form for next use
    this.tagNameFieldTarget.value = ""
    this.previewTarget.classList.add("hidden")
    this.previewContentTarget.innerHTML = ""
    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.textContent = "Assign Property"

    const searchInput = this.element.querySelector("[data-autocomplete-target='input']")
    if (searchInput) searchInput.value = ""
  }

  _showErrors(messages) {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.innerHTML = messages.map(m => `<p>${m}</p>`).join("")
    this.errorsTarget.classList.remove("hidden")
  }

  _hideErrors() {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.classList.add("hidden")
    this.errorsTarget.innerHTML = ""
  }

  _showFlash(message) {
    const container = document.createElement("div")
    container.className = "flash-toast-notice"
    container.dataset.controller = "flash"
    container.dataset.flashDismissAfterValue = "5000"
    container.setAttribute("role", "alert")

    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = `<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>`

    const msgEl = document.createElement("p")
    msgEl.className = "flex-1 text-sm"
    msgEl.textContent = message

    container.appendChild(iconDiv)
    container.appendChild(msgEl)
    document.body.insertBefore(container, document.body.firstChild)
  }
}
